<script setup>
import { $api } from "@/utils/api";
import {
  maskCpfOrCnpj,
  maskCep,
  maskRg,
  maskTelefone,
  onlyDigits,
} from "@/utils/masks";

definePage({
  meta: {
    requiresAuth: true,
  },
});

const router = useRouter();

const codigo = ref("");
const form = ref({
  NOME: "",
  DATA_NASCIMENTO: "",
  CPF: "",
  RG: "",
  FONE1: "",
  ENDERECO: "",
  NUMERO: "",
  CEP: "",
  COD_CIDADE: null,
  COD_UF: null,
  COD_FUNCAO: null,
  TERCEIRO: "N",
  COD_TIPO_TERCEIRO: null,
  ATIVO: 1,
});

const funcoesOptions = ref([]);
const cidadesOptions = ref([]);
const ufsOptions = ref([]);
const tiposTerceiroOptions = ref([]);

const loading = ref(true);
const salvando = ref(false);
const cpfDuplicado = ref(false);
const cpfValidando = ref(false);
const modalSucesso = ref(false);
const erros = ref({});

// Máscaras e apenas dígitos nos campos
const cpfDisplay = computed({
  get: () => maskCpfOrCnpj(form.value.CPF),
  set: (val) => {
    form.value.CPF = onlyDigits(val).slice(0, 14);
    cpfDuplicado.value = false;
  },
});
const rgDisplay = computed({
  get: () => maskRg(form.value.RG),
  set: (val) => {
    form.value.RG = onlyDigits(val).slice(0, 9);
  },
});
const telefoneDisplay = computed({
  get: () => maskTelefone(form.value.FONE1),
  set: (val) => {
    form.value.FONE1 = onlyDigits(val).slice(0, 11);
  },
});
const cepDisplay = computed({
  get: () => maskCep(form.value.CEP),
  set: (val) => {
    form.value.CEP = onlyDigits(val).slice(0, 8);
  },
});

const cpfDigits = computed(() => onlyDigits(form.value.CPF));
const cpfValido = computed(() => {
  const d = cpfDigits.value;
  return d.length === 0 || d.length === 11 || d.length === 14;
});

const podeConcluir = computed(() => {
  if (!form.value.NOME?.trim()) return false;
  if (form.value.TERCEIRO !== "S" && form.value.TERCEIRO !== "N") return false;
  if (form.value.ATIVO !== 0 && form.value.ATIVO !== 1) return false;
  if (cpfDuplicado.value) return false;
  if (cpfDigits.value.length > 0 && !cpfValido.value) return false;
  return true;
});

async function fetchProximoCodigo() {
  try {
    const res = await $api("/prestadores/proximo-codigo");
    codigo.value = res?.codigo ?? "";
  } catch (e) {
    codigo.value = "";
  }
}

async function fetchFuncoes() {
  try {
    const list = await $api("/funcoes");
    funcoesOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    funcoesOptions.value = [];
  }
}

async function fetchCidades() {
  try {
    const list = await $api("/cidades");
    cidadesOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    cidadesOptions.value = [];
  }
}

async function fetchEstados() {
  try {
    const list = await $api("/estados");
    ufsOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    ufsOptions.value = [];
  }
}

async function fetchTiposTerceiro() {
  try {
    const list = await $api("/tipos-terceiro");
    tiposTerceiroOptions.value = Array.isArray(list) ? list : [];
  } catch (e) {
    tiposTerceiroOptions.value = [];
  }
}

async function verificarCpfDuplicado() {
  const d = cpfDigits.value;
  if (d.length !== 11 && d.length !== 14) {
    cpfDuplicado.value = false;
    return;
  }
  cpfValidando.value = true;
  try {
    const res = await $api(`/prestadores/verificar-cpf?cpf=${d}`);
    cpfDuplicado.value = res?.existe ?? false;
  } catch (e) {
    cpfDuplicado.value = false;
  } finally {
    cpfValidando.value = false;
  }
}

function onCpfBlur() {
  verificarCpfDuplicado();
}

function bloqueiaNaoNumericoCpf(e) {
  const key = e.key;
  if (/[0-9]/.test(key)) return;
  const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (allowed.includes(key)) return;
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(key.toLowerCase())) return;
  e.preventDefault();
}

function bloqueiaNaoNumericoRg(e) {
  const key = e.key;
  if (/[0-9]/.test(key)) return;
  const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (allowed.includes(key)) return;
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(key.toLowerCase())) return;
  e.preventDefault();
}

function bloqueiaNaoNumericoFone(e) {
  const key = e.key;
  if (/[0-9]/.test(key)) return;
  const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (allowed.includes(key)) return;
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(key.toLowerCase())) return;
  e.preventDefault();
}

function bloqueiaNaoNumericoCep(e) {
  const key = e.key;
  if (/[0-9]/.test(key)) return;
  const allowed = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "Home", "End"];
  if (allowed.includes(key)) return;
  if ((e.ctrlKey || e.metaKey) && ["a", "c", "v", "x"].includes(key.toLowerCase())) return;
  e.preventDefault();
}

function cancelar() {
  router.push({ name: "prestadores" });
}

async function concluir() {
  if (!podeConcluir.value) return;
  erros.value = {};
  const payload = {
    NOME: form.value.NOME?.trim() || null,
    DATA_NASCIMENTO: form.value.DATA_NASCIMENTO || null,
    CPF: form.value.CPF ? String(form.value.CPF).replace(/\D/g, "") : null,
    RG: form.value.RG || null,
    FONE1: form.value.FONE1 || null,
    ENDERECO: form.value.ENDERECO?.trim() || null,
    NUMERO: form.value.NUMERO?.trim() || null,
    CEP: form.value.CEP || null,
    COD_CIDADE: form.value.COD_CIDADE ?? null,
    COD_UF: form.value.COD_UF ?? null,
    COD_FUNCAO: form.value.COD_FUNCAO ?? null,
    TERCEIRO: form.value.TERCEIRO,
    COD_TIPO_TERCEIRO: form.value.COD_TIPO_TERCEIRO ?? null,
    ATIVO: form.value.ATIVO,
  };
  if (payload.ENDERECO && !payload.NUMERO) payload.NUMERO = "S/N";

  salvando.value = true;
  try {
    await $api("/prestadores", { method: "POST", body: payload });
    modalSucesso.value = true;
  } catch (e) {
    const data = e?.data;
    if (data?.errors) erros.value = data.errors;
    else erros.value = { _: [data?.message || "Erro ao cadastrar prestador."] };
  } finally {
    salvando.value = false;
  }
}

function fecharModalSucesso() {
  modalSucesso.value = false;
  router.push({ name: "prestadores" });
}

onMounted(async () => {
  loading.value = true;
  await Promise.all([fetchProximoCodigo(), fetchFuncoes(), fetchCidades(), fetchEstados(), fetchTiposTerceiro()]);
  loading.value = false;
});
</script>

<template>
  <div>
    <VBtn
      class="mb-4"
      variant="text"
      :to="{ name: 'prestadores' }"
    >
      <VIcon icon="tabler-arrow-left" class="me-1" />
      Voltar
    </VBtn>

    <VCard v-if="loading">
      <VCardText class="text-center py-8">
        <VProgressCircular indeterminate color="primary" />
        <p class="mt-2 text-body-2">Carregando...</p>
      </VCardText>
    </VCard>

    <VCard v-else>
      <VCardTitle class="text-h5 pa-4">Novo Prestador</VCardTitle>
      <VDivider />
      <VCardText class="pa-4">
        <VRow>
          <VCol cols="12" md="4">
            <VTextField
              :model-value="codigo"
              label="Código"
              disabled
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="8">
            <VTextField
              v-model="form.NOME"
              label="Nome"
              required
              density="compact"
              variant="outlined"
              :error-messages="erros.NOME"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="form.DATA_NASCIMENTO"
              label="Data de Nascimento"
              type="date"
              density="compact"
              variant="outlined"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="cpfDisplay"
              label="CPF / CNPJ"
              placeholder="Somente números (11 ou 14)"
              density="compact"
              variant="outlined"
              :error-messages="
                cpfDuplicado
                  ? ['Este CPF/CNPJ já está cadastrado.']
                  : cpfDigits.length > 0 && !cpfValido
                    ? ['Informe 11 (CPF) ou 14 (CNPJ) dígitos.']
                    : erros.CPF
              "
              :loading="cpfValidando"
              maxlength="18"
              @keydown="bloqueiaNaoNumericoCpf"
              @blur="onCpfBlur"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="rgDisplay"
              label="RG"
              placeholder="9 dígitos"
              density="compact"
              variant="outlined"
              maxlength="12"
              :error-messages="erros.RG"
              @keydown="bloqueiaNaoNumericoRg"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="telefoneDisplay"
              label="Telefone"
              placeholder="(00) 00000-0000"
              density="compact"
              variant="outlined"
              maxlength="15"
              :error-messages="erros.FONE1"
              @keydown="bloqueiaNaoNumericoFone"
            />
          </VCol>
          <VCol cols="12" md="6">
            <VTextField
              v-model="form.ENDERECO"
              label="Endereço"
              density="compact"
              variant="outlined"
              :error-messages="erros.ENDERECO"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="form.NUMERO"
              label="Número"
              placeholder="Ou S/N se sem número"
              density="compact"
              variant="outlined"
              :error-messages="erros.NUMERO"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VTextField
              v-model="cepDisplay"
              label="CEP"
              placeholder="8 dígitos"
              density="compact"
              variant="outlined"
              maxlength="9"
              :error-messages="erros.CEP"
              @keydown="bloqueiaNaoNumericoCep"
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_CIDADE"
              label="Cidade"
              :items="cidadesOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_UF"
              label="UF"
              :items="ufsOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
            />
          </VCol>
          <VCol cols="12" md="4">
            <VSelect
              v-model="form.COD_FUNCAO"
              label="Função"
              :items="funcoesOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
            />
          </VCol>
          <VCol cols="12" md="6">
            <VRadioGroup
              v-model="form.TERCEIRO"
              label="Terceiro"
              inline
              density="compact"
              :error-messages="erros.TERCEIRO"
            >
              <VRadio label="Não" value="N" />
              <VRadio label="Sim" value="S" />
            </VRadioGroup>
          </VCol>
          <VCol v-if="form.TERCEIRO === 'S'" cols="12" md="6">
            <VSelect
              v-model="form.COD_TIPO_TERCEIRO"
              label="Tipo de Terceiro"
              :items="tiposTerceiroOptions"
              item-title="title"
              item-value="value"
              density="compact"
              variant="outlined"
              clearable
            />
          </VCol>
          <VCol cols="12" md="6">
            <VRadioGroup
              v-model="form.ATIVO"
              label="Status"
              inline
              density="compact"
              :error-messages="erros.ATIVO"
            >
              <VRadio :label="'Ativo'" :value="1" />
              <VRadio :label="'Inativo'" :value="0" />
            </VRadioGroup>
          </VCol>
        </VRow>

        <div v-if="erros._" class="mt-2">
          <VAlert type="error" density="compact" variant="tonal">
            {{ erros._[0] }}
          </VAlert>
        </div>

        <div class="d-flex gap-2 mt-4">
          <VBtn
            color="primary"
            :disabled="!podeConcluir"
            :loading="salvando"
            @click="concluir"
          >
            Concluir
          </VBtn>
          <VBtn variant="outlined" :disabled="salvando" @click="cancelar">
            Cancelar
          </VBtn>
        </div>
      </VCardText>
    </VCard>

    <!-- Modal sucesso -->
    <VDialog v-model="modalSucesso" max-width="400" persistent>
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon icon="tabler-circle-check" color="success" class="me-2" />
          Sucesso
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="fecharModalSucesso">
            <VIcon icon="tabler-x" />
          </VBtn>
        </VCardTitle>
        <VCardText>
          Prestador cadastrado com sucesso!
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn color="primary" variant="tonal" @click="fecharModalSucesso">
            Fechar
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
